/* --------------------------------------------------------------------- */
/* Fechas. El reporte razona en días completos de Ecuador, sin importar   */
/* la zona horaria del equipo desde el que se consulta.                   */
/*                                                                        */
/* Módulo puro, sin React ni DOM: es lo que permite ejercitar estas       */
/* reglas en una prueba sin montar la página.                             */
/* --------------------------------------------------------------------- */

export const TZ = 'America/Guayaquil';

// Antigüedad, en minutos, a partir de la cual una sala se considera sin datos y
// luego caída. Son los mismos valores por defecto que aplica el backend en
// DEFAULT_OPS; el servidor los devuelve en /fleet y manda sobre estos.
export const DEFAULT_FRESHNESS = { sinDatosMin: 15, caidoMin: 60 };

export function zoneOffset(reference) {
  const label = new Intl.DateTimeFormat('en-US', { timeZone: TZ, timeZoneName: 'longOffset' })
    .formatToParts(reference)
    .find((part) => part.type === 'timeZoneName')?.value;
  const match = /GMT([+-][0-9]{2}:[0-9]{2})/.exec(label || '');
  return match ? match[1] : '-05:00';
}

export function dayValue(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(value));
}

export function startOfDay(day) {
  return new Date(`${day}T00:00:00.000${zoneOffset(new Date(`${day}T12:00:00Z`))}`).toISOString();
}

export function endOfDay(day) {
  return new Date(`${day}T23:59:59.999${zoneOffset(new Date(`${day}T12:00:00Z`))}`).toISOString();
}

export function shiftDay(day, delta) {
  const [year, month, date] = day.split('-').map(Number);
  const moved = new Date(Date.UTC(year, month - 1, date));
  moved.setUTCDate(moved.getUTCDate() + delta);
  return moved.toISOString().slice(0, 10);
}

export function rangeForDays(days) {
  const today = dayValue(new Date());
  return { from: startOfDay(shiftDay(today, -(days - 1))), to: endOfDay(today) };
}

// Ventana móvil que termina ahora, no en el borde del día. «Hoy» a las 00:30
// tiene media hora de datos; «últimas 24 h» siempre tiene un día completo, que
// es lo que hace falta para no perder de vista la madrugada.
export function rangeForHours(hours) {
  const now = new Date();
  return {
    from: new Date(now.getTime() - hours * 3600000).toISOString(),
    to: now.toISOString(),
  };
}

// Ventana de un tramo horario del día en curso, en hora de Ecuador. Se usa para
// la madrugada, que es el momento de riesgo real por respiración nocturna.
export function rangeForHourWindow(startHour, endHour) {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  const build = (day) => {
    const offset = zoneOffset(new Date(`${day}T12:00:00Z`));
    // Un tramo que cruza la medianoche (18:00 a 06:00) termina al día siguiente.
    const endDay = endHour <= startHour ? shiftDay(day, 1) : day;
    return {
      from: new Date(`${day}T${pad(startHour)}:00:00.000${offset}`),
      to: new Date(`${endDay}T${pad(endHour)}:00:00.000${offset}`),
    };
  };

  const today = dayValue(now);
  let window = build(today);
  // A las 10:00, el turno noche «de hoy» aún no ha empezado. Lo que se quiere
  // ver es la noche que acaba de pasar, no una ventana vacía en el futuro.
  if (window.from.getTime() > now.getTime()) window = build(shiftDay(today, -1));

  return { from: window.from.toISOString(), to: window.to.toISOString() };
}

export function formatDay(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('es-EC', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(value));
}

export function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-EC', {
    timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function formatTimeAxis(value) {
  const date = new Date(value);
  const day = date.toLocaleDateString('es-EC', { timeZone: TZ, day: '2-digit', month: '2-digit' });
  const time = date.toLocaleTimeString('es-EC', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  return `${day} ${time}`;
}

export function rangeLabel(from, to) {
  const start = dayValue(from);
  const end = dayValue(to);
  if (!start || !end) return 'Todo el histórico';
  return start === end ? formatDay(from) : `${formatDay(from)} al ${formatDay(to)}`;
}

export function relativeTime(value) {
  if (!value) return 'sin lecturas';
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff)) return 'sin lecturas';
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'hace instantes';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} d`;
}

// Duración en lenguaje corriente. El tiempo acumulado bajo umbral se lee mejor
// como «1 h 45 min» que como 105.
export function formatDuration(minutes) {
  if (minutes === null || minutes === undefined || !Number.isFinite(Number(minutes))) return '—';
  const total = Math.max(0, Math.round(Number(minutes)));
  if (total === 0) return 'sin registro';
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function freshness(value, thresholds = DEFAULT_FRESHNESS) {
  if (!value) return 'critical';
  const minutes = (Date.now() - new Date(value).getTime()) / 60000;
  if (minutes <= thresholds.sinDatosMin) return 'ok';
  if (minutes <= thresholds.caidoMin) return 'warning';
  return 'critical';
}

export function bucketLabel(ms) {
  if (!ms) return '';
  if (ms < 3600000) return `${Math.round(ms / 60000)} min`;
  if (ms < 86400000) return `${Math.round(ms / 3600000)} h`;
  return `${Math.round(ms / 86400000)} d`;
}

// Mover un extremo por delante del otro invertiría el rango; en vez de
// rechazarlo, se arrastra el extremo contrario.
export function clampRange(range, field, iso) {
  const next = { ...range, [field]: iso };
  if (new Date(next.from) > new Date(next.to)) {
    if (field === 'from') next.to = endOfDay(dayValue(iso));
    else next.from = startOfDay(dayValue(iso));
  }
  return next;
}
